import { Link, useNavigate } from "react-router-dom";
import { Mail, Check, UserPlus } from "lucide-react";
import { EmptyState, ActionButton, SecondaryAction, TextLink } from "./kit";
import { coachSetupInviteHref } from "../lib/coachSetup";

/**
 * The redrawn empty coach portal (issue #756, wave 1 of epic #749) — a
 * coach with `rosterLoaded && players.length === 0`. Extracted out of
 * `CoachPortal.jsx` rather than inlined behind `newUi("coach-portal-empty")`
 * there: that function was already at the 800-line
 * `max-lines-per-function` budget in `eslint.config.mjs` before this slice
 * (see `CoachPortalShortcuts.jsx`'s header for the same reasoning, same
 * budget), so a redrawn screen's worth of markup written inline would have
 * deepened the warning the budget exists to stop growing.
 *
 * THE COPY WAS LOCKED (#284, restated on #756); ONE LINE OF IT IS NOW
 * CLIENT-RULED OPEN (#805, decided by Andreas 2026-09-23: "change the
 * words"). "No players yet." and the mechanism paragraph are still
 * byte-identical to what `CoachPortal.jsx` renders at its own
 * `cp-empty-roster` (see that file's shipped branch, kept verbatim behind
 * the flag-off leg). The "invite them yourself" paragraph's link text is
 * NOT byte-identical any more — Martin's own words named "Settings"
 * because that is where the invite lived when #284 was written; the
 * product moved the destination under it (item 1 below), so the words now
 * say where the link actually goes instead of a place it no longer opens.
 * Andreas ruled rather than wait on Martin's answer (asked 05:52, same
 * day). The client proposed different wording for the OTHER two lines on
 * 2026-09-19 and withdrew it the same day; this file must never carry that
 * withdrawn line.
 *
 * WHAT ACTUALLY CHANGES (both `approved-change`, #756, plus #805's ruling):
 *   1. The link's and the gold action's destination — `/coach-portal`'s
 *      old link went to `/settings`; both now open
 *      `coachSetupInviteHref()` (`lib/coachSetup.js`), which resolves to
 *      the invite section of the coach's own first-run set-up (#755),
 *      `/coach-setup#coach-setup-invite` — but ONLY when that screen is
 *      itself on. #795 review round F1 (general problem filed as #800):
 *      `newUi` is per-screen, so "`coach-portal-empty` on, `coach-setup`
 *      off" is a legal, reachable state, and the raw path is a dead link
 *      in it (`App.js:273-275` bounces `/coach-setup` back to
 *      `/coach-portal` whenever its own flag is off). The helper falls
 *      back to `/settings?open=more#settings-add-player` (Settings, More open, Add a player) in that state, so
 *      the coach's one action on this screen never dead-ends. THE WORDS
 *      now say "from your coach set-up" (#805) rather than naming either
 *      real destination literally — true in the state we ship and demo
 *      (`all`, both screens on): the link opens the coach set-up, and the
 *      words say so. NOT TRUE IN THE MIXED STATE (`coach-portal-empty` on,
 *      `coach-setup` off, reachable only in a per-screen rollout, never
 *      shown to the client): there the words still say "coach set-up" but
 *      `coachSetupInviteHref()` falls back to `/settings?open=more#settings-add-player`. That is the
 *      inverse of #805's original defect (words said "Settings", link
 *      opened coach set-up) rather than its absence — #800's resolver owns
 *      keeping the DESTINATION safe in that state; nothing here keeps the
 *      WORDS honest in it, same as before. This screen no longer explains
 *      the old (opposite-direction) mismatch out loud either (issue #805
 *      item 1/2): a help line doing so used to sit under the gold action
 *      and it contradicted the locked card copy a finger above it —
 *      removed rather than reworded, back when #284 was still what was
 *      locked. See issue #805 for the finding and the ruling.
 *   2. The pending-invites section now always renders SOMETHING — the real
 *      card when there's something pending, a labelled empty box when
 *      there isn't — instead of being entirely absent when empty.
 *      `cp-invites-card` is asserted present in BOTH states now, never
 *      `not.toBeVisible()`/absent for zero invites. #795 review round F2:
 *      the first cut of this also moved the POPULATED card's position,
 *      from above the empty-roster block (where the shipped
 *      `CoachPortal.jsx` puts it, and where this screen's own drawing
 *      still puts it) to below the gold action — three problems from one
 *      misplacement (R-003 broke, the drawing was misread as endorsing the
 *      move, and the screen's own copy — "it lands here… above the card"
 *      — stopped being true). Fixed by keeping the two states in the two
 *      POSITIONS the drawing actually draws them in: the real card above
 *      `cp-empty-roster` (present only when `invites.length > 0`, exactly
 *      shipped's own placement), the dashed labelled-empty box below the
 *      gold action (present only when there is nothing pending — the one
 *      spot the drawing shows it, and the spot the "above the card" copy
 *      is talking about). Never both markers at once: `cp-invites-card` is
 *      unique by construction (`invites.length` picks exactly one
 *      position to render into), not by picking a single shared slot.
 *
 *      THE POPULATED CARD'S COLOUR WAS UNCHANGED (byte-for-byte, the
 *      shipped `#D4A017`-tinted card), and #794 recorded why: the state was
 *      undrawn, so R-003 did not bind it. Round 5 draws it: `CoachPortalEmpty
 *      .dc.html`'s `invite-waiting` commentary state (D33, restated as
 *      audit finding G7.4/#870 and ruled D173.C3) IS the artboard for this
 *      state now, and #794's own reasoning — "R-003 does not bind an
 *      element the artboard never drew" — reverses once one exists. The
 *      card is now the neutral one D33 draws: white, `--nw-line` border,
 *      no gold tint at all. Accept (`cp-invite-accept-<id>`) is the ONE
 *      gold element D33 gives this state (`ActionButton`, `minHeight: 48`
 *      — the drawn 48px, not the kit's own 52px `--nw-size-action`, and
 *      `flexGrow: 1`/`width: "auto"` so it shares its row with Decline
 *      rather than spanning it). Decline (`cp-invite-decline-<id>`) is now
 *      a bare navy underlined text link (`SecondaryAction variant="link"`),
 *      not the shipped outline `<Button>` — D33: "Decline is a text link".
 *      "Invite a player" (`cp-empty-invite-btn`) drops to navy with a white
 *      label the moment an invite is pending, for the same reason D33
 *      gives: accepting is what this state is for, so gold names ONE
 *      action, not two. `sendRowExtra`-style prop threading was not needed
 *      here — unlike `AddPlayerCard`'s Send/Skip row (#869/D173.C2), this
 *      screen owns both elements itself.
 *
 *      journey 29 (`29-gold-count.journey.spec.js`)'s "ONE pending invite"
 *      case used to declare the shipped card's own gold tint as a named,
 *      tracked exclusion (`assertGoldMatchesInviteCase`, #794's follow-up)
 *      precisely because that tint was undrawn. It no longer needs one:
 *      the rebuilt card carries no gold at all, so the row now grades
 *      against the same default `assertAtMostOneGold` bound every other
 *      screen in that file uses — Accept is that one gold element, and a
 *      genuinely new gold source anywhere else on this screen still fails
 *      it, same as before, just without a hand-maintained exclusion list.
 *
 * `coach-portal-empty-new-screen` is a marker this component renders and
 * the shipped `cp-empty-roster` branch in `CoachPortal.jsx` does not — the
 * epic's per-screen-slice leg-assertion rule (comment on #749,
 * `assertCoachSetUpScreenMatchesLeg`'s own header), so a journey can prove
 * which leg actually rendered instead of trusting shared testids two
 * screens both carry.
 */
export default function CoachPortalEmptyState({ invites, inviteBusyId, onAcceptInvite, onDeclineInvite }) {
  const nav = useNavigate();

  // Issue #797 fix round, B1 (review) — this component is the redrawn
  // composition of `/coach-portal`, and the ONLY one whose marked content
  // has been measured (`frontend/e2e/text-size-tokens.spec.js`) to stay
  // fixed px at Compact, Default and Large.
  //
  // Issue #767 PR #920 review round 2, R1 — this component USED TO declare
  // its own `retiredByScreen` (`useRetireTextSize().setRetired(true)` on
  // mount, `(false)` on unmount) precisely because `Layout.jsx` could not
  // see `CoachPortal.jsx`'s own `rosterLoaded && players.length === 0`
  // gate. That made TWO independent writers of one boolean: the moment the
  // roster went from 0 to 1 in place (accepting an invite), THIS
  // component's cleanup fired `setRetired(false)` and nothing set it back
  // true — `CoachSquad.jsx` (the OTHER redrawn composition, when
  // `newUi("squad")` is also on) had already stopped declaring it too
  // (round 1's own fix), so the drawn chrome silently reverted to the
  // shipped header until a reload. `CoachPortal.jsx` is now the ONE owner,
  // computed from `lib/coachChromeGate.js`'s pure `computeDrawnCoachChrome`
  // — see that file and `CoachPortal.jsx`'s own `wantsDrawnCoachChrome`
  // effect. This component declares nothing itself any more.
  return (
    // Issue #792 — `space-y-6` (Tailwind, `rem`) rendered this page's own
    // stack gap at 25.5px against CoachPortalEmpty.dc.html:56's drawn 14px,
    // and moved with the text-size control. `.nw-page-stack` (index.css)
    // is fixed px from `--nw-space-stack`.
    <div className="nw-page-stack">
      <span data-testid="coach-portal-empty-new-screen" className="sr-only" aria-hidden="true" />

      {/* Issue #827 — `mt-1`/`mt-2` (Tailwind, `rem`) rendered this block's
          gaps at 8.75/8.5px, moving with the text-size control (though this
          screen's own control is retired — see `useRetireTextSize` above,
          the storage key still moves the root font-size the same way), against
          CoachPortalEmpty.dc.html:57's drawn `gap: 4px` — NOT the 6px
          CoachSetUp.dc.html:61 draws for its own header block, a different
          screen. `.nw-stack-xtight` (index.css) is fixed px from the
          existing `--nw-space-xtight` token (4px), the same class
          CoachSetUp.jsx's own "About you" group already uses.
          THE EYEBROW IS A `<div>`, NOT A `<span>` — see CoachSetUp.jsx's
          own comment on the identical fix: an inline eyebrow as the
          stack's first child measured a root-font-dependent gap (7.5px at
          Compact, 9.5px at Default/Large, not the drawn static 4px) via
          the anonymous line box's own containing-block-inherited
          line-height, not the margin-top token; `<div>` removes it,
          matching `EmptyState.jsx`'s own `eyebrow` (already a `<div>`). */}
      <div className="nw-stack-xtight">
        {/* Issue #804, decided by Andreas 2026-09-23: "the drawing wins" —
            eyebrow "Your players", title "Coach portal", exactly as
            `CoachPortalEmpty.dc.html:58-59` draws them. This screen
            (following #756 item 1 literally, per #795 review F6) had them
            reversed; #756 item 1 was the error, not the drawing. */}
        <div className="nw-eyebrow">Your players</div>
        <h1 className="nw-title">Coach portal</h1>
        {/* Issue #804 fix round, F2 — `CoachPortalEmpty.dc.html:60` draws
            this lede at 13px/#64748B; `.nw-lede` is 14px/`--nw-ink-lede`
            (#475569), off on both axes (measured live: 14px, rgb(71, 85,
            105)). `.nw-prose` is the kit's own 13px/400/line-height-1.5
            class (`index.css`, #771 fix round F11) — colour-agnostic by
            design, same as `.nw-caption`/`.nw-caption-strong`, so the
            drawn `--nw-ink-muted` (#64748B) is supplied here at the call
            site. Not a literal `text-[13px]` (the `CoachSetUp.jsx:271`
            precedent) — that pattern is for when no kit class fits; one
            already does here. */}
        <p className="nw-prose" style={{ color: "var(--nw-ink-muted)" }}>
          The squad by role opens here once a player is linked.
        </p>
      </div>

      {/* #795 review round, F2 — the REAL populated invites card, restored
          to the position the drawing (and the shipped `CoachPortal.jsx`)
          both put it in: above the empty-roster block, only when there is
          something pending. This is the ONLY place `cp-invites-card`
          renders when `invites.length > 0` — see the dashed labelled-empty
          branch further down for the `invites.length === 0` case, which is
          a DIFFERENT position, not a ternary sharing this one.

          Audit G7 (#870), D173.C3: rebuilt to D33's `invite-waiting` state
          (`CoachPortalEmpty.dc.html:92-116`) — a neutral card (no gold
          tint, this file's header explains why #794's exclusion no longer
          applies), a gold Accept, and Decline as a bare text link. Same
          handlers, same testids (`cp-invite-accept-<id>`/
          `cp-invite-decline-<id>`) as the shipped card this replaces. */}
      {invites.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: 14,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
          }}
          data-testid="cp-invites-card"
        >
          <div className="flex items-center" style={{ gap: 8 }}>
            <Mail size={15} color="var(--nw-navy)" aria-hidden="true" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--nw-navy)",
              }}
            >
              Pending invites ({invites.length})
            </span>
          </div>
          <div className="flex flex-col" style={{ gap: 8 }} data-testid="cp-invites-list">
            {invites.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  padding: 12,
                  background: "var(--nw-page)",
                  border: "1px solid var(--nw-line)",
                  borderRadius: "var(--nw-radius-control)",
                }}
                data-testid={`cp-invite-${inv.id}`}
              >
                <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{inv.player_full_name}</div>
                <div className="flex items-center" style={{ gap: 16 }}>
                  <ActionButton
                    onClick={() => onAcceptInvite(inv)}
                    disabled={inviteBusyId === inv.id}
                    icon={<Check size={18} aria-hidden="true" />}
                    style={{ minHeight: 48, flexGrow: 1, width: "auto" }}
                    data-testid={`cp-invite-accept-${inv.id}`}
                  >
                    Accept
                  </ActionButton>
                  <SecondaryAction
                    variant="link"
                    onClick={() => onDeclineInvite(inv)}
                    disabled={inviteBusyId === inv.id}
                    style={{ textDecoration: "underline", whiteSpace: "nowrap" }}
                    data-testid={`cp-invite-decline-${inv.id}`}
                  >
                    Decline
                  </SecondaryAction>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issue #284/#530, copy locked — see this file's header. `cp-empty-
          roster` sits on THIS card, same meaning as the shipped
          `CoachPortal.jsx` branch: the empty-roster explanation, not the
          whole composed screen — journey 02 asserts text against it
          directly. */}
      <EmptyState
        shape="portal"
        data-testid="cp-empty-roster"
        illustration="squad"
        line="No players yet."
        prose={
          <>
            <p>
              A player shares their record with you from their Settings — you'll get an invite here to accept. Once
              you're linked, their goals and feedback, plus your own reflections, all live on this page.
            </p>
            <p className="mt-3">
              You can also invite a player yourself:{" "}
              {/* Issue #805, decided by Andreas 2026-09-23: "change the
                  words" — Martin's #284 line named "Settings" because that
                  is where the invite lived when it was written; the
                  product moved and the words now say where the link
                  actually goes (the invite step of the coach set-up),
                  never "Settings". Only the destination noun changes; the
                  drawn sentence shape ("add them by email from your ___")
                  is kept (`CoachPortalEmpty.dc.html:69`, itself carrying
                  the pre-ruling words). */}
              {/* #1394 (D221 G4): a sentence link takes its hit area from padding
                  (`TextLink inline`), so the drawn text does not move. Vertically that
                  is `TAP_INLINE_LINK_STYLE`. A link that wraps is measured per line,
                  and a line only a word or two long is under 44px wide, so the
                  padding is also sideways, cloned on every line (`box-decoration-break`)
                  and cancelled by an equal negative margin: the words wrap where they
                  did and nothing moves. */}
              <TextLink
                as={Link}
                inline
                to={coachSetupInviteHref()}
                className="font-semibold underline"
                style={{
                  color: "var(--nw-navy)",
                  paddingInline: "calc(var(--nw-size-tap) / 2)",
                  marginInline: "calc(var(--nw-size-tap) / -2)",
                  boxDecorationBreak: "clone",
                  WebkitBoxDecorationBreak: "clone",
                }}
                data-testid="cp-empty-add-player-link"
              >
                add them by email from your coach set-up
              </TextLink>
              .
            </p>
          </>
        }
      />

      {/* Issue #756 item 4 — the one gold element on this screen (rule
          R-003): an empty portal exists to be filled. Same destination as
          the link above. `ActionButton` has no `as`/link mode (unlike
          `SecondaryAction`), so this navigates imperatively rather than
          rendering an `<a>` — every other gold action on a chromeless #750
          screen is a submit, not a navigation, so this is the first call
          site that needs it. Issue #793: the local `width: "100%"` is
          gone — `ActionButton` spans its row by default now.

          D173.C3: with an invite pending, Accept (above) is the one gold
          element the state is for — this button drops to navy with a
          white label, `CoachPortalEmpty.dc.html:113-116`'s
          `invite-waiting` state. The icon's own colour is set explicitly
          (its wrapper span, inside `ActionButton`, hardcodes `--nw-ink`
          otherwise — a `style` override on the button itself can't reach
          it). Unchanged, no invites pending: gold, ink icon, as before. */}
      <ActionButton
        onClick={() => nav(coachSetupInviteHref())}
        icon={<UserPlus size={20} color={invites.length > 0 ? "#FFFFFF" : "var(--nw-ink)"} aria-hidden="true" />}
        data-testid="cp-empty-invite-btn"
        style={invites.length > 0 ? { background: "var(--nw-navy)", color: "#FFFFFF" } : undefined}
      >
        Invite a player
      </ActionButton>

      {/* Issue #756 item 5, decided — do not re-open (see this file's
          header, point 2). The labelled-empty box: the ONE position it
          renders in, and only when `invites.length === 0` — the drawing's
          own spot for it, and the spot its own copy ("above the card")
          is talking about (the real card, above, in the OTHER branch
          further up). Not the shipped page's precedent — nothing rendered
          here before #756. */}
      {invites.length === 0 && (
        // Issue #792 — `space-y-1.5` (Tailwind, `rem`) rendered this box's
        // internal gap at 6.38px against CoachPortalEmpty.dc.html:78's
        // drawn 6px. `.nw-stack-tight` (index.css) is fixed px from the
        // new `--nw-space-tight` token — this box's own `padding` was
        // already a literal px pair and needed no change.
        <div
          className="nw-stack-tight"
          style={{
            padding: "12px 14px",
            background: "var(--nw-surface)",
            border: "1px dashed var(--nw-line-strong)",
            borderRadius: "var(--nw-radius-card)",
          }}
          data-testid="cp-invites-card"
        >
          <div className="nw-section" style={{ color: "var(--nw-ink-faint)" }}>
            Invites waiting on you
          </div>
          {/* Audit G7.2 (#870), D33's own correction: the shipped line said
              the invite "lands here … above the card", which was never
              true — it arrives at the TOP of the page (measured, #805
              third comment: the box at 734, the card at 304). The drawing
              was corrected to say where it actually appears, and this
              string now matches it verbatim. */}
          <p className="nw-help">
            None. When a player shares their record with you first, the invite appears at the top of this page.
          </p>
        </div>
      )}
    </div>
  );
}
